import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Clock, Award, ArrowRight, CheckCircle2, Briefcase, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { careers } from '../mockData';

export const CareerDetail = () => {
  const { id } = useParams();
  const career = careers.find(c => c.id === id);

  if (!career) {
    return <Navigate to="/carreras" replace />;
  }

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="relative h-[400px] overflow-hidden">
        <img
          src={career.image}
          alt={career.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <span className="inline-block px-4 py-2 bg-mit-red text-white font-semibold rounded-full mb-4">
                {career.category}
              </span>
              <h1 className="text-5xl font-bold text-white mb-4">{career.name}</h1>
              <p className="text-xl text-gray-200 mb-6">{career.description || career.shortDescription}</p>
              <div className="flex flex-wrap gap-4 text-white">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <Clock className="w-5 h-5" />
                  <span className="font-semibold">Duración: {career.duration}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <Award className="w-5 h-5" />
                  <span className="font-semibold">Modalidad: {career.modality}</span>
                </div>
                {career.convalidtionAvailable && (
                  <div className="flex items-center gap-2 bg-green-500/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-semibold">Convalidción Disponible</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Perfil Profesional */}
            {career.profile && (
              <Card>
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-mit-blue/10 rounded-lg flex items-center justify-center">
                      <GraduationCap className="w-6 h-6 text-mit-blue" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Perfil Profesional</h2>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{career.profile}</p>
                </CardContent>
              </Card>
            )}

            {/* Campo Laboral */}
            {career.fieldOfWork && (
              <Card>
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-mit-red/10 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-mit-red" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Campo Laboral</h2>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {career.fieldOfWork.map((field, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-mit-blue flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{field}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Malla Curricular */}
            {career.curriculum && (
              <Card>
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Malla Curricular</h2>
                  <Accordion type="single" collapsible className="w-full">
                    {career.curriculum.map((sem) => (
                      <AccordionItem key={sem.semester} value={`semester-${sem.semester}`}>
                        <AccordionTrigger className="text-lg font-semibold text-gray-900">
                          Semestre {sem.semester}
                        </AccordionTrigger>
                        <AccordionContent>
                          <ul className="space-y-2 pl-4">
                            {sem.courses.map((course, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-gray-700">
                                <CheckCircle2 className="w-4 h-4 text-mit-blue flex-shrink-0 mt-1" />
                                {course}
                              </li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Requisitos */}
            {career.requirements && (
              <Card className="sticky top-24">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Requisitos</h3>
                  <ul className="space-y-3">
                    {career.requirements.map((req, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-mit-blue flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{req}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 space-y-3">
                    <Link to="/contacto">
                      <Button className="w-full bg-mit-blue hover:bg-blue-800 text-white">
                        Solicitar Información
                      </Button>
                    </Link>
                    {career.convalidtionAvailable && (
                      <Link to="/convalidacion">
                        <Button variant="outline" className="w-full border-mit-red text-mit-red hover:bg-mit-red hover:text-white">
                          Convalidr Experiencia
                        </Button>
                      </Link>
                    )}
                    <a
                      href="https://wa.me/51944748443?text=Hola, visité la web y me gustaría mas información."
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="outline" className="w-full">
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        WhatsApp
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Volver */}
        <div className="mt-12">
          <Link to="/carreras" className="inline-flex items-center text-mit-blue hover:text-blue-800 font-semibold">
            <ArrowRight className="w-5 h-5 mr-2 rotate-180" />
            Volver a Carreras
          </Link>
        </div>
      </div>
    </div>
  );
};